/* eslint-disable react/prop-types */
// SolyticsStepper.jsx
// @ts-nocheck
/**
 * SolyticsStepper - Production-grade stepper component with multiple variants
 *
 * A flexible stepper component that supports three visual variants (filled, outlined, modern)
 * with optional navigation arrows, clickable steps, and full WCAG 2.2 AA compliance.
 *
 * @component
 *
 * @example
 * // Basic usage with filled variant
 * <SolyticsStepper
 *   steps={[{ label: 'Step 1' }, { label: 'Step 2' }, { label: 'Step 3' }]}
 *   activeStep={0}
 *   onStepClick={handleStepClick}
 * />
 *
 * @param {Object} props - Component props
 * @param {Array<{label: string}>} props.steps - Array of step objects with label property
 * @param {number} props.activeStep - Current active step index (0-based)
 * @param {('filled'|'outlined'|'modern')} props.variant - Visual style variant (default: 'filled')
 * @param {(stepIndex: number) => void} props.onStepClick - Callback when step is clicked/activated
 * @param {boolean} props.clickableSteps - Enable step clicking (default: false)
 *
 * @returns {React.ReactElement|null} Rendered stepper component or null if no steps
 */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { IconButton } from '@mui/material';
import SolyticsBox from './SolyticsBox';
import SolyticsChip from './SolyticsChip';
import SolyticsTypography from './SolyticsTypography';
import SolyticsTooltip from './SolyticsTooltip';
import { ReactComponent as CompletedStateIcon } from '../assets/CompletedState.svg';
import { ReactComponent as CurrentStateIcon } from '../assets/CurrentState.svg';
import { ReactComponent as PendingIcon } from '../assets/PendingIcon.svg';
import { ReactComponent as LeftArrowIcon } from '../assets/LeftArrowIcon.svg';
import { ReactComponent as RightArrowIcon } from '../assets/RightArrowIcon.svg';

// ============================================================================
// COMMON STYLING CONSTANTS
// ============================================================================

const MAX_LABEL_WIDTH = {
  filled: '160px',
  outlined: '140px',
  modern: '180px',
};

const MIN_SCROLL_DISTANCE = 120;
const SCROLL_DISTANCE_RATIO = 0.75;

/** Common focus-visible styles applied to interactive elements */
const FOCUS_VISIBLE_STYLES = {
  outline: 'none',
  '&:focus-visible': {
    outline: (theme) => `2px solid ${theme.palette.primary.main}`,
    outlineOffset: '2px',
  },
};

/** Common step circle sizing and layout */
const STEP_CIRCLE_BASE = {
  display: 'flex',
  width: '20px',
  height: '20px',
  minWidth: '20px',
  minHeight: '20px',
  justifyContent: 'center',
  alignItems: 'center',
  borderRadius: '50%',
  aspectRatio: '1/1',
  flexShrink: 0,
};

/** Common step container base styles */
const STEP_CONTAINER_BASE = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  flexShrink: 0,
  whiteSpace: 'nowrap',
};

/** Common navigation button styles */
const NAV_BUTTON_STYLES = {
  width: 32,
  height: 32,
  borderRadius: '4px',
  ...FOCUS_VISIBLE_STYLES,
};

// ============================================================================
// HELPER COMPONENTS
// ============================================================================

/**
 * StepCircle - Renders the visual indicator for a step
 * Handles completed, active, and pending states with appropriate icons/colors
 */
const StepCircle = React.memo(
  ({ isCompleted, isActive, showNumber, stepNumber, variant }) => {
    // Icon circle for filled variant
    if (variant === 'filled') {
      const bgColor = (theme) => {
        if (isCompleted) return theme.palette.success.main;
        if (isActive) return theme.palette.skyBlue.icon;
        return theme.palette.orange[60];
      };

      return (
        <SolyticsBox
          sx={{
            ...STEP_CIRCLE_BASE,
            padding: '3px',
            background: bgColor,
            color: (theme) => theme.palette.common.white,
          }}
          aria-hidden="true"
        >
          {isCompleted && <CompletedStateIcon />}
          {isActive && <CurrentStateIcon />}
          {!isCompleted && !isActive && <PendingIcon />}
        </SolyticsBox>
      );
    }

    // Numbered circle for outlined and modern variants
    if (showNumber) {
      const isOutlined = variant === 'outlined';
      const isModern = variant === 'modern';

      const borderStyle = (theme) => {
        if (isOutlined) return `1px solid ${theme.palette.primary.main}`;
        if (isModern) {
          return isCompleted
            ? 'none'
            : `1px solid ${theme.palette.skyBlue[50]}`;
        }
        return 'none';
      };

      const bgColor = (theme) => {
        if (isCompleted) {
          return isOutlined
            ? theme.palette.primary.main
            : theme.palette.success.main;
        }
        if (isModern) return theme.palette.skyBlue[25];
        return theme.palette.background.paper;
      };

      const textColor = (theme) => {
        if (isCompleted) {
          return isOutlined
            ? theme.palette.primary.contrastText
            : theme.palette.success.contrastText;
        }
        if (isModern) return theme.palette.skyBlue[50];
        return theme.palette.primary.main;
      };

      return (
        <SolyticsBox
          sx={{
            ...STEP_CIRCLE_BASE,
            border: borderStyle,
            background: bgColor,
            color: textColor,
          }}
        >
          <SolyticsTypography variant="captionMedium" color="inherit">
            {stepNumber + 1}
          </SolyticsTypography>
        </SolyticsBox>
      );
    }

    return null;
  }
);

StepCircle.displayName = 'StepCircle';

StepCircle.propTypes = {
  isCompleted: PropTypes.bool,
  isActive: PropTypes.bool,
  showNumber: PropTypes.bool,
  stepNumber: PropTypes.number,
  variant: PropTypes.oneOf(['filled', 'outlined', 'modern']),
};

/**
 * StepLabel - Renders the text label for a step with appropriate styling
 */
const StepLabel = React.memo(({ label, isActive, isPending, variant }) => {
  const getTextColor = (theme) => {
    if (variant === 'filled') {
      if (isPending) return theme.palette.text.disabled;
      if (isActive) return theme.palette.text.primary;
      return theme.palette.grey.T60;
    }
    return theme.palette.grey.T60;
  };

  return (
    <SolyticsBox
      sx={{
        color: getTextColor,
        minWidth: 0,
        maxWidth: MAX_LABEL_WIDTH[variant],
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      <SolyticsTypography variant="body2" noWrap>
        {label}
      </SolyticsTypography>
    </SolyticsBox>
  );
});

StepLabel.displayName = 'StepLabel';

StepLabel.propTypes = {
  label: PropTypes.string.isRequired,
  isActive: PropTypes.bool,
  isPending: PropTypes.bool,
  variant: PropTypes.oneOf(['filled', 'outlined', 'modern']),
};

/**
 * StepItem - Individual step element with tooltip, click handling, and styling
 */
const StepItem = React.memo(
  ({
    step,
    index,
    isCompleted,
    isActive,
    isPending,
    variant,
    clickableSteps,
    onStepClick,
    onStepKeyDown,
    maxHeight = '100%',
  }) => {
    const showNumber = variant !== 'filled';
    const isSummaryStep = Boolean(step?.isSummary);

    const handleClick = useCallback(() => {
      onStepClick(index);
    }, [index, onStepClick]);

    const handleKeyDown = useCallback(
      (e) => {
        onStepKeyDown(e, index);
      },
      [index, onStepKeyDown]
    );

    const getAriaLabel = () => {
      if (isSummaryStep) {
        return `Summary step: ${step.label}`;
      }
      let status = '';
      if (isCompleted) status = ' (completed)';
      else if (isActive) status = ' (current)';
      else if (isPending) status = ' (pending)';
      return `Step ${index + 1}: ${step.label}${status}`;
    };

    const summaryTooltipContent = useMemo(() => {
      const tooltipItems = Array.isArray(step?.tooltipItems)
        ? step.tooltipItems.filter(Boolean)
        : [];

      if (!tooltipItems.length) {
        return null;
      }

      return (
        <SolyticsBox component="ul" sx={{ m: 0, pl: 2 }}>
          {tooltipItems.map((item) => (
            <SolyticsBox component="li" key={item} sx={{ mb: 0.25 }}>
              {item}
            </SolyticsBox>
          ))}
        </SolyticsBox>
      );
    }, [step?.tooltipItems]);

    const stepContainerSx = useMemo(() => {
      const baseStyles = {
        ...STEP_CONTAINER_BASE,
        cursor: clickableSteps ? 'pointer' : 'default',
        ...FOCUS_VISIBLE_STYLES,
        maxHeight: maxHeight,
      };

      // Variant-specific styles
      if (variant === 'filled') {
        return {
          ...baseStyles,
          padding: '0 16px',
          borderRadius: isActive ? '8px 8px 0 0' : '0',
          // backgroundColor: (theme) =>
          //   isActive ? theme.palette.background.paper : 'transparent', //commenting for vault specification
          '&:hover': clickableSteps
            ? {
                opacity: 0.8,
                backgroundColor: (theme) =>
                  isActive
                    ? theme.palette.background.paper
                    : theme.palette.grey[50],
              }
            : {},
        };
      }

      // Outlined and modern variants
      return {
        ...baseStyles,
        '&:hover': clickableSteps ? { opacity: 0.85 } : {},
      };
    }, [variant, clickableSteps, isActive]);

    if (isSummaryStep) {
      return (
        <SolyticsTooltip
          variant="info"
          title={step?.tooltipTitle || 'Upcoming Next Steps'}
          subtitle={summaryTooltipContent}
          placement="top"
          arrow
        >
          <SolyticsBox
            aria-label={getAriaLabel()}
            sx={{
              ...STEP_CONTAINER_BASE,
              cursor: 'default',
              ...FOCUS_VISIBLE_STYLES,
            }}
          >
            <SolyticsChip
              label={step?.label}
              shape="pill"
              variant="outlined"
              size="small"
              color="secondary"
            />
          </SolyticsBox>
        </SolyticsTooltip>
      );
    }

    return (
      <SolyticsTooltip text={step.label} placement="top" arrow>
        <SolyticsBox
          role="button"
          tabIndex={clickableSteps ? 0 : -1}
          aria-current={isActive ? 'step' : undefined}
          aria-label={getAriaLabel()}
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          sx={stepContainerSx}
        >
          <StepCircle
            isCompleted={isCompleted}
            isActive={isActive}
            showNumber={showNumber}
            stepNumber={index}
            variant={variant}
          />
          <StepLabel
            label={step.label}
            isActive={isActive}
            isPending={isPending}
            variant={variant}
          />
        </SolyticsBox>
      </SolyticsTooltip>
    );
  }
);

StepItem.displayName = 'StepItem';

StepItem.propTypes = {
  maxHeight: PropTypes.string,
  step: PropTypes.shape({
    label: PropTypes.string.isRequired,
    isSummary: PropTypes.bool,
    tooltipTitle: PropTypes.string,
    tooltipItems: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  index: PropTypes.number.isRequired,
  isCompleted: PropTypes.bool,
  isActive: PropTypes.bool,
  isPending: PropTypes.bool,
  variant: PropTypes.oneOf(['filled', 'outlined', 'modern']),
  clickableSteps: PropTypes.bool,
  onStepClick: PropTypes.func.isRequired,
  onStepKeyDown: PropTypes.func.isRequired,
};

/**
 * NavigationButton - Arrow button for step navigation
 */
const NavigationButton = React.memo(({ direction, disabled, onClick }) => {
  const isLeft = direction === 'left';
  const Icon = isLeft ? LeftArrowIcon : RightArrowIcon;
  const buttonColor = (theme) =>
    disabled ? theme.palette.text.disabled : theme.palette.text.primary;

  return (
    <IconButton
      onClick={onClick}
      disabled={disabled}
      aria-label={isLeft ? 'Scroll stepper left' : 'Scroll stepper right'}
      size="small"
      sx={{
        ...NAV_BUTTON_STYLES,
        color: buttonColor,
      }}
    >
      <Icon />
    </IconButton>
  );
});

NavigationButton.propTypes = {
  direction: PropTypes.oneOf(['left', 'right']),
  disabled: PropTypes.bool,
  onClick: PropTypes.func,
};

NavigationButton.displayName = 'NavigationButton';

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const SolyticsStepper = ({
  steps = [],
  activeStep = 0,
  variant = 'filled',
  onStepClick,
  clickableSteps = false,
  hideBorder = false, // for vault use case where stepper is inside a card and border would be redundant
  maxHeight = '100%',
}) => {
  const scrollContainerRef = useRef(null);
  const [hasOverflow, setHasOverflow] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // WCAG-compliant step click handler
  const handleStepClick = useCallback(
    (stepIndex) => {
      if (clickableSteps && onStepClick) {
        onStepClick(stepIndex);
      }
    },
    [clickableSteps, onStepClick]
  );

  // WCAG keyboard navigation (Enter/Space keys)
  const handleStepKeyDown = useCallback(
    (event, stepIndex) => {
      if (
        clickableSteps &&
        onStepClick &&
        (event.key === 'Enter' || event.key === ' ')
      ) {
        event.preventDefault();
        onStepClick(stepIndex);
      }
    },
    [clickableSteps, onStepClick]
  );

  const updateScrollState = useCallback(() => {
    const node = scrollContainerRef.current;

    if (!node || variant !== 'filled') {
      setHasOverflow(false);
      setCanScrollLeft(false);
      setCanScrollRight(false);
      return;
    }

    const nextHasOverflow = node.scrollWidth - node.clientWidth > 1;
    const nextCanScrollLeft = node.scrollLeft > 1;
    const nextCanScrollRight =
      node.scrollLeft + node.clientWidth < node.scrollWidth - 1;

    setHasOverflow(nextHasOverflow);
    setCanScrollLeft(nextCanScrollLeft);
    setCanScrollRight(nextHasOverflow && nextCanScrollRight);
  }, [variant]);

  useEffect(() => {
    const node = scrollContainerRef.current;

    updateScrollState();

    if (!node || variant !== 'filled') {
      return undefined;
    }

    const handleResize = () => {
      updateScrollState();
    };

    node.addEventListener('scroll', updateScrollState, { passive: true });
    window.addEventListener('resize', handleResize);

    let resizeObserver;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        updateScrollState();
      });
      resizeObserver.observe(node);
    }

    return () => {
      node.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', handleResize);
      resizeObserver?.disconnect();
    };
  }, [steps.length, updateScrollState, variant]);

  const handleScroll = useCallback((direction) => {
    const node = scrollContainerRef.current;

    if (!node) {
      return;
    }

    const scrollDistance = Math.max(
      MIN_SCROLL_DISTANCE,
      node.clientWidth * SCROLL_DISTANCE_RATIO
    );

    node.scrollBy({
      left: direction === 'left' ? -scrollDistance : scrollDistance,
      behavior: 'smooth',
    });
  }, []);

  const handleScrollLeft = useCallback(() => {
    handleScroll('left');
  }, [handleScroll]);

  const handleScrollRight = useCallback(() => {
    handleScroll('right');
  }, [handleScroll]);

  // Render filled variant with icon-based step indicators
  if (variant === 'filled') {
    return (
      <SolyticsBox
        sx={{
          display: 'flex',
          width: '100%',
          height: '36px',
          alignItems: 'center',
          gap: '8px',
          borderRadius: '4px',
          ...(hideBorder
            ? {}
            : { border: (theme) => `1px solid ${theme.palette.divider}` }),
          background: (theme) => theme.palette.background.default, //commenting for vault specification
        }}
      >
        {hasOverflow && (
          <NavigationButton
            direction="left"
            disabled={!canScrollLeft}
            onClick={handleScrollLeft}
          />
        )}

        <SolyticsBox
          role="group"
          aria-label="Progress stepper"
          ref={scrollContainerRef}
          sx={{
            flex: 1,
            height: '36px',
            borderRadius: '4px',
            display: 'flex',
            overflow: 'auto',
            overflowY: 'hidden',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            '&::-webkit-scrollbar': {
              display: 'none',
            },
          }}
        >
          {steps.map((step, index) => {
            const isCompleted = index < activeStep;
            const isActive = index === activeStep;
            const isPending = index > activeStep;

            return (
              <StepItem
                key={`filled-${step.label}`}
                step={step}
                index={index}
                isCompleted={isCompleted}
                isActive={isActive}
                isPending={isPending}
                variant="filled"
                clickableSteps={clickableSteps}
                onStepClick={handleStepClick}
                onStepKeyDown={handleStepKeyDown}
                maxHeight={maxHeight}
              />
            );
          })}
        </SolyticsBox>

        {hasOverflow && (
          <NavigationButton
            direction="right"
            disabled={!canScrollRight}
            onClick={handleScrollRight}
          />
        )}
      </SolyticsBox>
    );
  }

  // Render outlined variant with numbered step indicators
  if (variant === 'outlined') {
    return (
      <SolyticsBox
        sx={{
          display: 'flex',
          width: '100%',
          alignItems: 'center',
          gap: '8px',
        }}
      >
        <SolyticsBox
          role="group"
          aria-label="Progress stepper"
          sx={{
            flex: 1,
            height: '36px',
            borderRadius: '4px',
            border: (theme) => `1px solid ${theme.palette.divider}`,
            backgroundColor: (theme) => theme.palette.background.paper,
            display: 'flex',
            alignItems: 'center',
            paddingTop: '8px',
            paddingBottom: '8px',
            paddingLeft: '16px',
            gap: '16px',
            overflow: 'auto',
            overflowY: 'hidden',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            '&::-webkit-scrollbar': {
              display: 'none',
            },
          }}
        >
          {steps.map((step, index) => {
            const isCompleted = index < activeStep;
            const isActive = index === activeStep;

            return (
              <StepItem
                key={`outlined-${step.label}`}
                step={step}
                index={index}
                isCompleted={isCompleted}
                isActive={isActive}
                isPending={false}
                variant="outlined"
                clickableSteps={clickableSteps}
                onStepClick={handleStepClick}
                onStepKeyDown={handleStepKeyDown}
                maxHeight={maxHeight}
              />
            );
          })}
        </SolyticsBox>
      </SolyticsBox>
    );
  }

  // Render modern variant with bordered circles and green/blue indicators
  if (variant === 'modern') {
    return (
      <SolyticsBox
        sx={{
          display: 'flex',
          width: '100%',
          alignItems: 'center',
          borderRadius: '4px',
          border: (theme) => `1px solid ${theme.palette.divider}`,
          background: (theme) => theme.palette.background.paper,
          padding: '8px 0px 8px 16px',
          gap: '24px',
          overflow: 'auto',
          overflowY: 'hidden',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
        }}
        role="group"
        aria-label="Progress stepper"
      >
        {steps.map((step, index) => {
          const isCompleted = index < activeStep;
          const isActive = index === activeStep;

          return (
            <StepItem
              key={`modern-${step.label}`}
              step={step}
              index={index}
              isCompleted={isCompleted}
              isActive={isActive}
              isPending={false}
              variant="modern"
              clickableSteps={clickableSteps}
              onStepClick={handleStepClick}
              onStepKeyDown={handleStepKeyDown}
              maxHeight={maxHeight}
            />
          );
        })}
      </SolyticsBox>
    );
  }
};

SolyticsStepper.propTypes = {
  maxHeight: PropTypes.string,
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  activeStep: PropTypes.number.isRequired,
  variant: PropTypes.oneOf(['filled', 'outlined', 'modern']),
  onStepClick: PropTypes.func,
  clickableSteps: PropTypes.bool,
  hideBorder: PropTypes.bool,
};

export default SolyticsStepper;
